import type {
  CsvRow,
  Player,
  PlayerComboEntry,
  PlayerPickDataPoint,
  PlayerWithExposure,
  PortfolioPair,
  PlayerCLVResult,
  TeamPlayerWithCLV,
  TeamCLVResult,
  TeamDraft,
} from "../types/draft";
import { UNDRAFTED_ADP_FALLBACK } from "../constants";
import { canonicalName, canonicalTeam } from "./playerIdentity";

/**
 * Resolve a raw ADP lookup value to a usable number. A missing or unparseable
 * ADP (null / undefined / NaN — e.g. Underdog reporting "-" for a still-draftable
 * player) falls back to the undrafted floor so CLV keeps computing. Single source
 * of truth for the fallback, shared by computePlayerCLV and the Insights path.
 */
export function resolveMarketAdp(raw: number | null | undefined): number {
  return typeof raw === "number" && !isNaN(raw) ? raw : UNDRAFTED_ADP_FALLBACK;
}

/** Convert Underdog "Picked At" value (e.g. "2026-04-10 23:54:05 UTC") to a Date.
 *  Safari requires strict ISO-8601 so we normalise to "2026-04-10T23:54:05Z". */
export function parsePickedAt(raw: string | undefined | null): Date | null {
  if (!raw) return null;
  const iso = raw.replace(" UTC", "Z").replace(" ", "T");
  const d = new Date(iso);
  return isNaN(d.getTime()) ? null : d;
}

/**
 * The cross-platform join key for a player name. Delegates to `canonicalName`,
 * which additionally folds diacritics, apostrophes, hyphens and given-name /
 * nickname pairs ("Kenneth Gainwell" ≡ "Kenny Gainwell") so an Underdog name and
 * a DraftKings name for the same human resolve to one key.
 *
 * Kept under the original export name because it is the join key for every
 * market/history/rookie lookup in the app.
 */
export const normalizePlayerName = canonicalName;

export function groupDataByPlayer(data: CsvRow[]): Player[] {
  const playerMap = new Map<string, Player>();
  data.forEach(row => {
    const first = (row["First Name"] || "").trim();
    const last = (row["Last Name"] || "").trim();
    const team = canonicalTeam(row["Team"]);
    const position = (row["Position"] || "").trim();
    const name = `${first} ${last}`.trim();
    if (!name) return;
    // Key on the CANONICAL name so "Marvin Harrison Jr." (DraftKings) and
    // "Marvin Harrison" (Underdog) land in one entry. Team and position stay in
    // the key as a disambiguation guard; `resolveIdentities` has already made
    // them agree across platforms for any portfolio that came through the
    // context, so they no longer split a single player.
    const key = `${canonicalName(name)}|${team}|${position}`;
    if (!playerMap.has(key)) {
      playerMap.set(key, { name, team, position, pickNumbers: [], rows: [] });
    }
    const player = playerMap.get(key)!;
    player.pickNumbers.push(Number(row["Pick Number"]) || 0);
    player.rows.push(row);
  });

  // Merge free-agent (blank team) entries into the real-team entry for the
  // same player. A player drafted as FA in some entries and team-affiliated
  // in others should be a single entry, not two.
  for (const [key, blankPlayer] of playerMap) {
    if (blankPlayer.team) continue;
    const realEntry = Array.from(playerMap.values()).find(
      p => p.team && p.name === blankPlayer.name && p.position === blankPlayer.position
    );
    if (realEntry) {
      realEntry.rows.push(...blankPlayer.rows);
      realEntry.pickNumbers.push(...blankPlayer.pickNumbers);
      playerMap.delete(key);
    }
  }
  Array.from(playerMap.values()).forEach(player => {
    const monthCounts: Record<string, number> = {};
    player.rows.forEach(row => {
      const pickedAt = row["Picked At"];
      if (pickedAt) {
        const date = parsePickedAt(pickedAt);
        if (date) {
          const monthStr = date.toLocaleString("default", { month: "long", year: "numeric" });
          monthCounts[monthStr] = (monthCounts[monthStr] || 0) + 1;
        }
      }
    });
    const tournamentCounts: Record<string, number> = {};
    player.rows.forEach(row => {
      const title = row["Tournament Title"] || "Unknown";
      tournamentCounts[title] = (tournamentCounts[title] || 0) + 1;
    });
    let totalTournamentEntryFee = 0;
    player.rows.forEach(row => {
      const fee = parseFloat(row["Tournament Entry Fee"] ?? "");
      if (!isNaN(fee)) totalTournamentEntryFee += fee;
    });
    player.accordionStats = {
      monthCounts,
      tournamentCounts,
      totalTournamentEntryFee,
    };
  });
  return Array.from(playerMap.values());
}

/**
 * Pre-index all players by draftId for O(1) lookup in getPlayerCombos.
 * Build once per allPlayers array change and reuse across all PlayerRow instances.
 */
export function buildPlayersByDraftIndex(allPlayers: Player[]): Map<string, Player[]> {
  const index = new Map<string, Player[]>();
  allPlayers.forEach(player => {
    player.rows.forEach((row: CsvRow) => {
      const draftId = row["Draft"];
      if (!draftId) return;
      if (!index.has(draftId)) index.set(draftId, []);
      index.get(draftId)!.push(player);
    });
  });
  return index;
}

interface MinimalPlayerRef {
  name: string;
  team: string;
  position: string;
}

export function getPlayerCombos(
  player: Player,
  allPlayers: Player[],
  comboSize = 2,
  topN = 5,
  playersByDraftIndex?: Map<string, Player[]>
): PlayerComboEntry[] {
  const playerDrafts: Record<string, boolean> = {};
  player.rows.forEach(row => {
    const draftId = row["Draft"];
    if (!draftId) return;
    playerDrafts[draftId] = true;
  });

  const playerKey = `${player.name}|${player.team}|${player.position}`;

  // Direct counting approach — avoids expensive recursive k_combinations.
  // For 2-combos: count each teammate across drafts — O(teammates × drafts).
  // For 3-combos: count each pair of teammates — O(teammates² × drafts).
  const combosCount = new Map<string, { players: MinimalPlayerRef[]; count: number }>();

  Object.keys(playerDrafts).forEach(draftId => {
    const rawPlayersInDraft = playersByDraftIndex
      ? (playersByDraftIndex.get(draftId) ?? [])
      : allPlayers.filter((p: Player) => p.rows.some((row: CsvRow) => row["Draft"] === draftId));

    // Deduplicate teammates (exclude the target player)
    const teammates: MinimalPlayerRef[] = [];
    const seen = new Set<string>();
    for (const p of rawPlayersInDraft) {
      const k = `${p.name}|${p.team}|${p.position}`;
      if (k === playerKey || seen.has(k)) continue;
      seen.add(k);
      teammates.push({ name: p.name, team: p.team, position: p.position });
    }

    if (comboSize === 2) {
      // Count each individual teammate
      for (const t of teammates) {
        const comboKey = `${t.name}|${t.team}|${t.position}`;
        const existing = combosCount.get(comboKey);
        if (existing) {
          existing.count += 1;
        } else {
          combosCount.set(comboKey, { players: [t], count: 1 });
        }
      }
    } else if (comboSize === 3) {
      // Count each pair of teammates
      for (let i = 0; i < teammates.length; i++) {
        for (let j = i + 1; j < teammates.length; j++) {
          const pair = [teammates[i], teammates[j]].sort((a, b) =>
            a.name.localeCompare(b.name) ||
            a.team.localeCompare(b.team) ||
            a.position.localeCompare(b.position)
          );
          const comboKey = pair.map(p => `${p.name}|${p.team}|${p.position}`).join("||");
          const existing = combosCount.get(comboKey);
          if (existing) {
            existing.count += 1;
          } else {
            combosCount.set(comboKey, { players: pair, count: 1 });
          }
        }
      }
    }
  });

  return Array.from(combosCount.values())
    .sort((a, b) => b.count - a.count)
    .slice(0, topN);
}

export function getPlayerPickChartData(player: Player): PlayerPickDataPoint[] {
  if (!player || !player.rows) return [];
  const points = player.rows
    .map(row => {
      const raw = row["Picked At"];
      const date = parsePickedAt(raw);
      const pick = Number(row["Pick Number"]);
      const provider = (row["Provider"] || "").trim() || "Unknown";
      return date && !isNaN(date.getTime()) && !isNaN(pick) ? { date, pick, provider } : null;
    })
    .filter((pt): pt is { date: Date; pick: number; provider: string } => pt !== null)
    .sort((a, b) => a.date.getTime() - b.date.getTime());
  return points.map(pt => ({
    pick: pt.pick,
    provider: pt.provider,
    date: pt.date.getTime(),
    dateObj: pt.date,
    dateStr: pt.date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }),
  }));
}

/**
 * Missing high-exposure combos: pairs of players you each roster a lot
 * individually but rarely stack together. For each pair where both players
 * clear `minExposure`, computes how often they actually appear together and
 * the unrealized overlap (gap). Sorted by the biggest gap first.
 *
 * Generalizes the old "never rostered together" view — those pairs simply have
 * togetherPct = 0 (the maximum possible gap).
 */
export function getMissingCombos(
  players: Player[],
  rawData: CsvRow[],
  minExposure = 15,
  topN = 12
): PortfolioPair[] {
  const totalDrafts = new Set(rawData.map(row => row["Draft"])).size;
  if (totalDrafts === 0) return [];

  const playerObjs: Array<PlayerWithExposure & { draftSet: Set<string> }> = players
    .map(player => {
      const draftSet = new Set(player.rows.map(row => row["Draft"]));
      const exposure = (draftSet.size / totalDrafts) * 100;
      return { ...player, exposure, draftSet };
    })
    .filter(p => p.exposure >= minExposure);

  const pairs: PortfolioPair[] = [];
  for (let i = 0; i < playerObjs.length; ++i) {
    for (let j = i + 1; j < playerObjs.length; ++j) {
      const p1 = playerObjs[i];
      const p2 = playerObjs[j];
      let together = 0;
      const [small, large] = p1.draftSet.size <= p2.draftSet.size ? [p1.draftSet, p2.draftSet] : [p2.draftSet, p1.draftSet];
      for (const d of small) if (large.has(d)) together++;
      const togetherPct = (together / totalDrafts) * 100;
      const gapPct = Math.min(p1.exposure, p2.exposure) - togetherPct;
      if (gapPct > 0) {
        pairs.push({ p1, p2, exposure1: p1.exposure, exposure2: p2.exposure, togetherPct, gapPct });
      }
    }
  }
  pairs.sort((a, b) => b.gapPct - a.gapPct || (b.exposure1 + b.exposure2) - (a.exposure1 + a.exposure2));
  return pairs.slice(0, topN);
}

// Single source of truth for computing CLV for one player.
// Returns raw numbers (or null); callers format for display.
export function computePlayerCLV(
  name: string,
  myPickNum: number,
  udAdpLookup: Map<string, number>
): PlayerCLVResult {
  const udKey = normalizePlayerName(name || "");
  const udAdp = resolveMarketAdp(udAdpLookup.get(udKey));
  const clv = !isNaN(myPickNum) ? myPickNum - udAdp : null;
  const clvPct = (typeof clv === "number" && udAdp !== 0) ? (clv / udAdp) * 100 : null;
  return { udAdp, clv, clvPct };
}

export function getTeamCLV(
  team: TeamDraft,
  udAdpLookup: Map<string, number>
): TeamCLVResult {
  const players = Array.isArray(team.players) ? team.players : [];
  let totalCLV = 0;
  const grouped: Record<string, typeof players> = {};
  players.forEach(p => {
    if (!grouped[p.position]) grouped[p.position] = [];
    grouped[p.position].push(p);
  });
  Object.values(grouped).forEach(arr =>
    arr.sort((a, b) => Number(a.pickNumber) - Number(b.pickNumber))
  );
  const positionOrder = ["QB", "RB", "WR", "TE", "K", "DST"];
  const orderedPositions = positionOrder
    .filter(pos => grouped[pos])
    .concat(Object.keys(grouped).filter(pos => !positionOrder.includes(pos)));
  const sortedPlayers = orderedPositions.reduce<typeof players>((acc, pos) => {
    if (grouped[pos]) acc.push(...grouped[pos]);
    return acc;
  }, []);
  const playerCLVs: TeamPlayerWithCLV[] = sortedPlayers.map(p => {
    const playerName = p.firstName && p.lastName
      ? `${p.firstName} ${p.lastName}`
      : (p.name || "");
    const myPick = Number(p.pickNumber);
    const { udAdp, clv } = computePlayerCLV(playerName, myPick, udAdpLookup);
    if (typeof clv === "number") totalCLV += clv;
    return { ...p, clv, udAdp };
  });
  return { totalCLV, playerCLVs };
}

/** The platform a player was drafted on most (ties → Underdog, then first seen).
 *  Used where a single per-player market must be chosen for display columns. */
export function dominantProvider(rows: CsvRow[]): string {
  const counts: Record<string, number> = {};
  for (const r of rows) {
    const p = (r["Provider"] || "").trim() || "Unknown";
    counts[p] = (counts[p] || 0) + 1;
  }
  const entries = Object.entries(counts);
  if (entries.length === 0) return "Unknown";
  entries.sort((a, b) => b[1] - a[1] || (a[0] === "Underdog" ? -1 : 1));
  return entries[0][0];
}

export interface PlayerCLVMulti {
  /** Mean CLV across all the player's picks, each scored on its OWN platform's
   *  market. Null when the player has no numeric picks. */
  avgClv: number | null;
  /** Per-provider breakdown: mean CLV + draft count, keyed by provider label. */
  byProvider: Record<string, { avgClv: number; drafts: number }>;
}

/**
 * CLV for one player across a mixed UD+DK portfolio. Each of the player's picks
 * is scored against its own platform's ADP (never blended), then averaged.
 * A player drafted on both platforms gets a `byProvider` split. This replaces
 * the old "average the pick numbers, then one CLV" path, which mixed the 216-
 * pick UD scale with the 240-pick DK scale.
 */
export function computePlayerCLVMulti(
  name: string,
  rows: CsvRow[],
  lookupForProvider: (provider?: string) => Map<string, number>
): PlayerCLVMulti {
  const key = normalizePlayerName(name || "");
  const perProv: Record<string, { sum: number; n: number }> = {};
  let sum = 0;
  let n = 0;
  for (const row of rows) {
    const pick = Number(row["Pick Number"]);
    if (isNaN(pick)) continue;
    const prov = (row["Provider"] || "").trim() || "Unknown";
    const adp = resolveMarketAdp(lookupForProvider(prov).get(key));
    const clv = pick - adp;
    sum += clv;
    n += 1;
    (perProv[prov] ??= { sum: 0, n: 0 });
    perProv[prov].sum += clv;
    perProv[prov].n += 1;
  }
  const byProvider: Record<string, { avgClv: number; drafts: number }> = {};
  for (const [prov, v] of Object.entries(perProv)) {
    byProvider[prov] = { avgClv: v.sum / v.n, drafts: v.n };
  }
  return { avgClv: n > 0 ? sum / n : null, byProvider };
}

// ── Projections ─────────────────────────────────────────────────────
// Parallel to the CLV helpers: a player's current projected season points and a
// team's cumulative (summed) projection. Source is adp.json's projectedPoints
// (Underdog's own projection). A name with no match — or no numeric projection —
// returns null and contributes nothing to the team total.

/** Resolve one player's projected season points from the projection lookup. */
export function computePlayerProjection(
  name: string,
  projectionLookup: Map<string, number>
): number | null {
  const key = normalizePlayerName(name || "");
  const proj = projectionLookup.get(key);
  return typeof proj === "number" && !isNaN(proj) ? proj : null;
}

/** Sum of every rostered player's projected season points. */
export function getTeamProjection(
  team: TeamDraft,
  projectionLookup: Map<string, number>
): number {
  const players = Array.isArray(team.players) ? team.players : [];
  let total = 0;
  for (const p of players) {
    const playerName = p.firstName && p.lastName
      ? `${p.firstName} ${p.lastName}`
      : (p.name || "");
    const proj = computePlayerProjection(playerName, projectionLookup);
    if (typeof proj === "number") total += proj;
  }
  return total;
}

